import { COUNTRY_OPTIONS, countryName } from "@lib/countryCodes";
import { X } from "lucide-react";
import { useState } from "react";

export interface SearchLocation {
  name: string;
  countryCode: string;
}

interface SearchLocationSelectProps {
  value: SearchLocation[];
  onChange: (locations: SearchLocation[]) => void;
  label?: string;
  hint?: string;
  /** Mirrors SearchLocatable::MAX_SEARCH_LOCATIONS. */
  max?: number;
}

const DEFAULT_MAX = 10;

export function SearchLocationSelect({
  value,
  onChange,
  label,
  hint,
  max = DEFAULT_MAX,
}: SearchLocationSelectProps) {
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState(COUNTRY_OPTIONS[0]?.code ?? "gb");

  const atCapacity = value.length >= max;
  const trimmed = name.trim();
  const duplicate = value.some(
    (l) => l.name.toLowerCase() === trimmed.toLowerCase() && l.countryCode === countryCode,
  );
  const canAdd = trimmed.length > 0 && !duplicate && !atCapacity;

  const handleAdd = () => {
    if (!canAdd) return;
    onChange([...value, { name: trimmed, countryCode }]);
    setName("");
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-1">
      {label && <label className="text-xs font-medium text-text-secondary">{label}</label>}
      {hint && <p className="text-xs text-text-muted">{hint}</p>}

      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {value.map((location, index) => (
            <span
              key={`${location.name}-${location.countryCode}`}
              className="inline-flex items-center gap-1 rounded bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent"
            >
              {location.name}
              <span className="text-accent/60">{countryName(location.countryCode)}</span>
              <button
                type="button"
                aria-label={`Remove ${location.name}`}
                onClick={() => handleRemove(index)}
                className="rounded-full p-0.5 hover:bg-accent/20"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1">
        <input
          type="text"
          value={name}
          disabled={atCapacity}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={atCapacity ? `Limit of ${max} reached` : "City or region, e.g. London"}
          aria-label="Location name"
          className="min-w-0 flex-1 rounded border border-input-border bg-card px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
        />
        <select
          value={countryCode}
          disabled={atCapacity}
          onChange={(e) => setCountryCode(e.target.value)}
          aria-label="Location country"
          className="rounded border border-input-border bg-card px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
        >
          {COUNTRY_OPTIONS.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className="rounded border border-border px-2 py-1 text-xs text-text-secondary hover:bg-accent/5 disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </div>
  );
}
